/**
 * The one card-tokenization seam (TMMAPP-0015). Fetches bearer client-config, loads Stripe.js,
 * mounts the card and its billing address (`mountCardElements`, TMMAPP-0119 §B / item 22), and
 * tokenizes. Never logs or stores the token, the card or the address.
 *
 * A card error is catalog copy only (TMMAPP-0119 item 20): the Element's validation `code` maps to
 * a sentence per field family, never Stripe's own text.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { billingClientConfigApi } from '@/api/billingClientConfig';
import { useThemeMode } from '@/context/theme-mode-context';
import { resolveTokens } from '@/theme';
import {
  clearStripeMemo,
  getStripe,
  mountCardElements,
  type CardElements,
  type CardElementTargets,
  type CardField,
} from './stripeSdk';

export type CardTokenStatus = 'loading' | 'notConfigured' | 'unsupported' | 'unavailable' | 'ready';

/**
 * `fieldError`: the refusal is already shown under the card fields (or by the Address Element), so
 * the host adds no banner of its own.
 */
export type TokenizeResult =
  { ok: true; token: string } | { ok: false; message: string | null; fieldError?: boolean };

export type CardFieldState = Record<CardField, { focused: boolean; invalid: boolean }>;

const NO_FIELDS: CardFieldState = {
  number: { focused: false, invalid: false },
  expiry: { focused: false, invalid: false },
  cvc: { focused: false, invalid: false },
};

type ErrorFamily = CardField | 'address' | 'other';

/**
 * Stripe's card and address error codes, each as the field it is about (TMMAPP-0122 item 28): an
 * explicit table, so a code like `expired_card` reads "Check the expiry date." instead of falling
 * to the generic line. A code not listed (`card_declined`, `processing_error`, …) is `other`.
 */
const CODE_FAMILIES: Readonly<Record<string, ErrorFamily>> = {
  incomplete_number: 'number',
  invalid_number: 'number',
  incorrect_number: 'number',
  incomplete_expiry: 'expiry',
  invalid_expiry_month: 'expiry',
  invalid_expiry_year: 'expiry',
  invalid_expiry_month_past: 'expiry',
  invalid_expiry_year_past: 'expiry',
  expired_card: 'expiry',
  incomplete_cvc: 'cvc',
  invalid_cvc: 'cvc',
  incorrect_cvc: 'cvc',
  incomplete_zip: 'address',
  invalid_zip: 'address',
  incorrect_zip: 'address',
  postal_code_invalid: 'address',
  incorrect_address: 'address',
};

/** A Stripe card or address validation `code`, as the field it is about. */
export function cardErrorFamily(code: string | null): ErrorFamily {
  if (code === null || !Object.hasOwn(CODE_FAMILIES, code)) return 'other';
  return CODE_FAMILIES[code]!;
}

/** The catalog sentence for the fields Stripe flags (item 20); several fields read as one. */
export function cardErrorText(t: TFunction, families: readonly ErrorFamily[]): string | null {
  if (families.length === 0) return null;
  if (new Set(families).size > 1) return t('card.fieldError.several');
  switch (families[0]) {
    case 'number':
      return t('card.fieldError.number');
    case 'expiry':
      return t('card.fieldError.expiry');
    case 'cvc':
      return t('card.fieldError.cvc');
    case 'address':
      return t('card.fieldError.address');
    default:
      return t('card.fieldError.other');
  }
}

/** The browser locale's region (`en-GB` → GB, a bare `fr` maximized to FR), as 0117 B7 reads it. */
export function billingCountryForLocale(locale: string | null | undefined): string | null {
  if (!locale) return null;
  try {
    return new Intl.Locale(locale).maximize().region ?? null;
  } catch {
    return null;
  }
}

export function useCardToken(targets: RefObject<CardElementTargets | null>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const { resolvedMode } = useThemeMode();
  const [status, setStatus] = useState<CardTokenStatus>('loading');
  const [complete, setComplete] = useState(false);
  const [fields, setFields] = useState<CardFieldState>(NO_FIELDS);
  // The Elements' current validation codes, per field; a refusal at tokenize replaces them.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<CardField, string>>>({});
  const [tokenizeError, setTokenizeError] = useState<ErrorFamily | null>(null);
  // The served statement descriptor (TMMAPP-0119 item 12): read from the same client config.
  const [statementDescriptor, setStatementDescriptor] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const cardRef = useRef<CardElements | null>(null);
  // Seeds the Elements' language and the default country at mount: a language change doesn't
  // re-mount (and clear) the card and the address.
  const languageRef = useRef(i18n.language);
  useEffect(() => {
    languageRef.current = i18n.language;
  });

  useEffect(() => {
    let cancelled = false;
    let card: CardElements | null = null;
    const completes: Record<CardField, boolean> = { number: false, expiry: false, cvc: false };

    async function setup() {
      setStatus('loading');
      setComplete(false);
      setFields(NO_FIELDS);
      setFieldErrors({});
      setTokenizeError(null);
      const result = await billingClientConfigApi.get();
      if (cancelled) return;
      if (result.kind === 'unsupported') {
        setStatus('unsupported');
        return;
      }
      if (result.kind === 'unavailable') {
        setStatus('unavailable');
        return;
      }
      setStatementDescriptor(result.config.statementDescriptor);
      const stripe = await getStripe(result.config.publishableKey, result.config.stripeAccount);
      if (cancelled) return;
      const mountTargets = targets.current;
      if (!stripe || !mountTargets) {
        setStatus('notConfigured');
        return;
      }
      const fontFamily = theme.typography.fontFamily;
      const font = typeof fontFamily === 'string' ? { fontFamily } : {};
      card = mountCardElements(stripe, mountTargets, {
        appearance: {
          theme: resolvedMode === 'dark' ? 'night' : 'stripe',
          variables: {
            // Stripe paints focus rings and selection accents with it — a non-text boundary
            // (TMMAPP-0046), so the served 3:1 boundary, not the entered brand.
            colorPrimary: tokens.brandBoundary,
            colorText: tokens.text,
            colorBackground: tokens.surface,
            ...font,
          },
        },
        // The body face (never mono) and the text tokens; the outlined box is the app's own.
        style: {
          base: {
            color: tokens.text,
            fontSize: '14px',
            lineHeight: '21px',
            ...font,
            '::placeholder': { color: tokens.text2 },
          },
          invalid: { color: tokens.text },
        },
        locale: languageRef.current,
        country: billingCountryForLocale(
          typeof navigator !== 'undefined' && navigator.language
            ? navigator.language
            : languageRef.current,
        ),
        onFieldChange: (field, fieldComplete, errorCode) => {
          completes[field] = fieldComplete;
          setComplete(completes.number && completes.expiry && completes.cvc);
          setFieldErrors((prev) => {
            const next = { ...prev };
            if (errorCode === null) delete next[field];
            else next[field] = errorCode;
            return next;
          });
          setFields((prev) => ({
            ...prev,
            [field]: { ...prev[field], invalid: errorCode !== null },
          }));
          setTokenizeError(null);
        },
        onFieldFocus: (field, focused) =>
          setFields((prev) => ({ ...prev, [field]: { ...prev[field], focused } })),
        // Fixing the address clears "Check the billing address." (item 27).
        onAddressChange: () => setTokenizeError((prev) => (prev === 'address' ? null : prev)),
      });
      cardRef.current = card;
      setStatus('ready');
    }

    void setup();
    return () => {
      cancelled = true;
      cardRef.current = null;
      card?.destroy();
    };
  }, [
    reloadKey,
    resolvedMode,
    tokens.brandBoundary,
    tokens.text,
    tokens.text2,
    tokens.surface,
    theme.typography.fontFamily,
    targets,
  ]);

  const tokenize = useCallback(async (): Promise<TokenizeResult> => {
    const card = cardRef.current;
    if (!card) return { ok: false, message: null };
    setTokenizeError(null);
    // cardToken is a Stripe PaymentMethod id (pm_…). If tmm-api rules it is a card token (tok_…),
    // switch `mountCardElements`' tokenize to stripe.createToken(number) → token.id.
    const result = await card.tokenize();
    if (result.ok) return { ok: true, token: result.paymentMethodId };
    // The Address Element shows its own errors; the line under the card names the address too.
    const family = result.reason === 'address' ? 'address' : cardErrorFamily(result.code);
    setTokenizeError(family);
    return { ok: false, message: null, fieldError: true };
  }, []);

  const retry = useCallback(() => {
    clearStripeMemo();
    setReloadKey((n) => n + 1);
  }, []);

  const families = Object.values(fieldErrors).map((code) => cardErrorFamily(code ?? null));
  const fieldMessage =
    tokenizeError !== null ? cardErrorText(t, [tokenizeError]) : cardErrorText(t, families);

  return { status, complete, fields, fieldMessage, statementDescriptor, tokenize, retry };
}
